/**
 * /akun/edit-preferensi — Edit Preferensi (plan 21).
 *
 * 3 section (TANPA kategorisasi produk — user tidak peduli worksheet
 * itu bagian produk apa):
 *   1. Umur (kelompok usia; default ikuti profil anak)
 *   2. Aktivitas (15 kanonik DB, multi-select 3-5)
 *   3. Jumlah worksheet/hari (1-5)
 *
 * Section Tingkat Kesulitan dihapus 2026-09-09 atas keputusan user:
 * preferensinya tersimpan tapi belum dikonsumsi algoritma rekomendasi
 * (dan data difficulty di tabel worksheet NULL semua) — pengertian
 * tingkat kesulitan menunggu kajian tim konten.
 *
 * Amandemen plan 21 (2026-08-23): halaman ini HANYA untuk premium user
 * (tier premium, gerbang entitlement plan 23). Sesi non-premium melihat
 * locked state; server menegaskan dengan 403 PremiumRequired di PUT.
 *
 * On mount, loads the current preferences via GET /v1/preferences/:id.
 * On save, calls PUT /v1/preferences/:id. preferredTema sengaja di-omit
 * dari payload (section Tema dihapus 2026-08-15; nilai legacy terjaga).
 */

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { AppIcon } from '@/components/app-icon';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { activityIcon } from '@/lib/icons';
import { getActiveChild } from '@/lib/api/children';
import { getPreferences, updatePreferences } from '@/lib/api/preferences';
import { loadSession } from '@/lib/parent-session';
import { getStoredAccount, isGuestSession } from '@/lib/auth';
import { preferencesVisibleForTier, tierForSession } from '@/lib/premium';
import { isPremiumCached } from '@/lib/premium-status';
import { setPremiumPromptState } from '@/lib/premium-prompt';
import { ApiError } from '@/lib/api/client';
import {
  ACTIVITY_LIST,
  ACTIVITY_MIN,
  ACTIVITY_MAX,
} from '@/lib/activity';
import { CLASS_OPTIONS, preferredClassPayload } from '@/lib/age-class';

import type { ChildProfile, ClassId, UserPreferences } from '@funpaper/shared';

const COUNT_MIN = 1;
const COUNT_MAX = 5;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'locked' }
  | { kind: 'ready'; child: ChildProfile; prefs: UserPreferences }
  | { kind: 'error'; message: string };

export default function EditPreferensiScreen() {
  const router = useRouter();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [selectedClass, setSelectedClass] = useState<ClassId>(1);
  const [activity, setActivity] = useState<string[]>([]);
  const [count, setCount] = useState(3);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // --------------------------------------------------------------------------
  // Load
  // --------------------------------------------------------------------------

  useEffect(() => {
    (async () => {
      const session = await loadSession();
      if (!session && !isGuestSession()) {
        router.replace('/akun/pin' as never);
        return;
      }
      // Fase 2 plan 23: preferensi lanjutan hanya untuk tier premium
      // (server juga menegaskan lewat 403 PremiumRequired — ini murni
      // visibilitas UI). isPremiumCached sync-read boleh stale sesaat;
      // cache di-refresh di Beranda/Akun.
      if (!preferencesVisibleForTier(tierForSession({ isGuest: isGuestSession(), isPremium: isPremiumCached() }))) {
        setState({ kind: 'locked' });
        return;
      }
      try {
        const child = await getActiveChild();
        if (!child) {
          setState({ kind: 'error', message: 'Belum ada profil anak.' });
          return;
        }
        const prefs = await getPreferences(child.id);
        setState({ kind: 'ready', child, prefs });
        setSelectedClass(prefs.preferredClassId ?? child.classId);
        setActivity(prefs.preferredActivity);
        setCount(prefs.dailyCount);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Gagal memuat preferensi.';
        setState({ kind: 'error', message });
      }
    })();
  }, [router]);

  // --------------------------------------------------------------------------
  // Toggles
  // --------------------------------------------------------------------------

  const toggleActivity = (name: string) => {
    setActivity((prev) => {
      if (prev.includes(name)) return prev.filter((s) => s !== name);
      if (prev.length >= ACTIVITY_MAX) return prev;
      return [...prev, name];
    });
  };

  // --------------------------------------------------------------------------
  // Save
  // --------------------------------------------------------------------------

  const onSave = useCallback(async () => {
    if (state.kind !== 'ready') return;
    if (activity.length < ACTIVITY_MIN) {
      setError(`Pilih minimal ${ACTIVITY_MIN} aktivitas.`);
      return;
    }
    if (activity.length > ACTIVITY_MAX) {
      setError(`Maksimal ${ACTIVITY_MAX} aktivitas.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // preferredTema sengaja di-omit (optional di server) supaya nilai
      // legacy user tidak tertimpa — section Tema sudah dihapus dari UI.
      // preferredClassId: null bila sama dengan kelas profil (ikuti profil).
      // difficulty juga di-omit — section dihapus (lihat header); nilai
      // tersimpan user tidak ditimpa.
      await updatePreferences(state.child.id, {
        preferredClassId: preferredClassPayload(selectedClass, state.child.classId),
        preferredActivity: activity,
        dailyCount: count as 1 | 2 | 3 | 4 | 5,
      });
      // Simpan apa pun dianggap "sudah tahu" — mematikan reminder Beranda.
      setPremiumPromptState(getStoredAccount()?.id ?? null, 'completed');
      setToast('✓ Preferensi disimpan');
      setTimeout(() => router.back(), 600);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Gagal menyimpan preferensi.';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [state, selectedClass, activity, count, router]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (state.kind === 'loading') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.kind === 'locked') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.header}>
            <Pressable
              onPress={() => router.back()}
              style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
              accessibilityRole="button"
              accessibilityLabel="Kembali"
            >
              <Text style={styles.backArrow}>←</Text>
            </Pressable>
            <ThemedText type="subtitle" style={styles.headerTitle}>Edit Preferensi</ThemedText>
            <View style={{ width: 40 }} />
          </View>
          <View style={styles.center}>
            <View style={styles.lockedCard}>
              <ThemedText type="smallBold" style={styles.lockedTitle}>
                Fitur Premium
              </ThemedText>
              <ThemedText type="small" themeColor="textSecondary" style={styles.lockedBody}>
                Pengaturan preferensi lanjutan (kelompok usia dan aktivitas)
                tersedia untuk akun premium. Masuk dan upgrade dulu yah.
              </ThemedText>
              <Pressable
                onPress={() => router.back()}
                style={({ pressed }) => [styles.retryBtn, pressed && styles.btnPressed]}
                accessibilityRole="button"
                accessibilityLabel="Kembali"
              >
                <Text style={styles.retryText}>Kembali</Text>
              </Pressable>
            </View>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.kind === 'error') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ThemedText type="subtitle" style={styles.errorTitle}>{state.message}</ThemedText>
            <Pressable onPress={() => router.back()} style={styles.retryBtn}>
              <Text style={styles.retryText}>Kembali</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <Text style={styles.backArrow}>←</Text>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Edit Preferensi</ThemedText>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent}>
          {/* 1. Umur */}
          <View style={styles.field}>
            <ThemedText type="smallBold" style={styles.label}>
              Umur
            </ThemedText>
            <ThemedText type="small" themeColor="textSecondary">
              Pilih kelompok usia untuk rekomendasi. Boleh ikuti profil anak.
            </ThemedText>
            <View style={styles.ageRow}>
              {CLASS_OPTIONS.map((option) => {
                const active = selectedClass === option.classId;
                const fromProfile = state.child.classId === option.classId;
                return (
                  <Pressable
                    key={option.classId}
                    onPress={() => setSelectedClass(option.classId)}
                    style={({ pressed }) => [
                      styles.ageChip,
                      active && styles.ageChipActive,
                      pressed && styles.btnPressed,
                    ]}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={`Kelompok usia ${option.label} ${option.ageLabel}`}
                  >
                    <Text style={[styles.ageChipLabel, active && styles.ageChipLabelActive]}>
                      {option.label}
                    </Text>
                    <Text
                      style={[
                        styles.ageChipSub,
                        active && styles.ageChipSubActive,
                      ]}
                    >
                      {option.ageLabel}
                    </Text>
                    {active && fromProfile ? (
                      <Text style={styles.ageFromProfile}>(dari profil)</Text>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* 2. Aktivitas */}
          <View style={styles.field}>
            <View style={styles.fieldHeader}>
              <ThemedText type="smallBold" style={styles.label}>
                Aktivitas
              </ThemedText>
              <ThemedText type="small" themeColor="textSecondary">
                {activity.length} dipilih · pilih {ACTIVITY_MIN}–{ACTIVITY_MAX}
              </ThemedText>
            </View>
            <ThemedText type="small" themeColor="textSecondary">
              Aktivitas favorit anak. Semua worksheet dipilih berdasarkan ini.
            </ThemedText>
            <View style={styles.activityList}>
              {ACTIVITY_LIST.map((a) => {
                const active = activity.includes(a.name);
                return (
                  <Pressable
                    key={a.name}
                    onPress={() => toggleActivity(a.name)}
                    style={({ pressed }) => [
                      styles.activityChip,
                      active && styles.activityChipActive,
                      pressed && styles.btnPressed,
                    ]}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: active }}
                    accessibilityLabel={`Aktivitas ${a.name}`}
                  >
                    <View style={styles.activityChipContent}>
                      <AppIcon source={activityIcon(a.name)} size={24} />
                      <Text style={[styles.chipLabel, active && styles.chipLabelActive]}>
                        {a.name}
                      </Text>
                    </View>
                    {active ? (
                      <View style={styles.selectedBadge}>
                        <Text style={styles.selectedBadgeText}>✓ Dipilih</Text>
                      </View>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* 3. Jumlah worksheet/hari */}
          <View style={styles.field}>
            <View style={styles.fieldHeader}>
              <ThemedText type="smallBold" style={styles.label}>
                Jumlah worksheet/hari: {count}
              </ThemedText>
              <ThemedText type="small" themeColor="textSecondary">
                {COUNT_MIN}–{COUNT_MAX}
              </ThemedText>
            </View>
            <View style={styles.countRow}>
              {([1, 2, 3, 4, 5] as const).map((c) => (
                <Pressable
                  key={c}
                  onPress={() => setCount(c)}
                  style={({ pressed }) => [
                    styles.countChip,
                    count === c && styles.countChipActive,
                    pressed && styles.btnPressed,
                  ]}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: count === c }}
                  accessibilityLabel={`${c} worksheet per hari`}
                >
                  <Text style={[styles.countChipText, count === c && styles.countChipTextActive]}>
                    {c}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>

          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          {toast ? <Text style={styles.toastText}>{toast}</Text> : null}
        </ScrollView>

        <View style={styles.bottomActions}>
          <Pressable
            onPress={onSave}
            disabled={saving}
            style={({ pressed }) => [styles.saveBtn, saving && styles.btnDisabled, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Simpan preferensi"
          >
            <Text style={styles.saveBtnText}>
              {saving ? 'Menyimpan…' : 'Simpan'}
            </Text>
          </Pressable>
        </View>
      </SafeAreaView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: 18, fontWeight: '700', color: '#1A0B5C' },
  scrollContent: { padding: Spacing.four, gap: Spacing.four, paddingBottom: Spacing.five },
  field: { gap: Spacing.two },
  fieldHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' },
  label: { color: '#1A0B5C', fontSize: 15 },
  ageRow: { flexDirection: 'row', gap: Spacing.two },
  ageChip: {
    flex: 1,
    paddingVertical: Spacing.two,
    paddingHorizontal: Spacing.two,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    alignItems: 'center',
    gap: 2,
    minHeight: 48,
    justifyContent: 'center',
  },
  ageChipActive: {
    backgroundColor: 'rgba(86, 63, 122, 0.10)',
    borderColor: Brand.primary,
    borderWidth: 2,
  },
  ageChipLabel: { fontSize: 14, fontWeight: '600', color: '#1A0B5C' },
  ageChipLabelActive: { color: Brand.primary, fontWeight: '700' },
  ageChipSub: { fontSize: 11, color: '#6b6573' },
  ageChipSubActive: { color: Brand.primary },
  ageFromProfile: { fontSize: 10, color: Brand.primary, fontWeight: '600' },
  chipLabel: { fontSize: 12, fontWeight: '500', color: '#1A0B5C', textAlign: 'center' },
  chipLabelActive: { color: Brand.primary, fontWeight: '700' },
  activityList: { gap: Spacing.two },
  activityChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    minHeight: 48,
  },
  activityChipActive: {
    backgroundColor: 'rgba(86, 63, 122, 0.10)',
    borderColor: Brand.primary,
    borderWidth: 2,
  },
  activityChipContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  selectedBadge: {
    marginLeft: Spacing.two,
    paddingHorizontal: Spacing.two,
    paddingVertical: Spacing.one,
    borderRadius: Radii.pill,
    backgroundColor: Brand.primary,
  },
  selectedBadgeText: { color: '#ffffff', fontSize: 11, fontWeight: '700' },
  countRow: { flexDirection: 'row', gap: Spacing.two },
  countChip: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    alignItems: 'center',
  },
  countChipActive: { backgroundColor: Brand.primary, borderColor: Brand.primary },
  countChipText: { fontSize: 18, fontWeight: '600', color: '#1A0B5C' },
  countChipTextActive: { color: '#ffffff' },
  errorText: { color: '#e53e3e', fontSize: 13, textAlign: 'center' },
  toastText: { color: Brand.primary, fontSize: 13, textAlign: 'center' },
  bottomActions: { padding: Spacing.four },
  saveBtn: {
    backgroundColor: Brand.primary,
    paddingVertical: 14,
    borderRadius: Radii.medium,
    alignItems: 'center',
  },
  saveBtnText: { color: '#ffffff', fontSize: 16, fontWeight: '700' },
  btnPressed: { opacity: 0.7 },
  btnDisabled: { opacity: 0.5 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: Spacing.four, gap: Spacing.three },
  errorTitle: { color: Brand.primary, textAlign: 'center' },
  lockedCard: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    gap: Spacing.two,
    alignItems: 'center',
    maxWidth: 420,
  },
  lockedTitle: { color: '#1A0B5C', fontSize: 16 },
  lockedBody: { textAlign: 'center', lineHeight: 19 },
  retryBtn: { marginTop: Spacing.two, paddingHorizontal: Spacing.four, paddingVertical: Spacing.two, backgroundColor: Brand.primary, borderRadius: Radii.medium },
  retryText: { color: '#ffffff', fontSize: 14, fontWeight: '600' },
});
